import { useEffect, useRef } from "react";
import { prefersReducedMotion } from "@/lib/animations";

/**
 * Lightweight animated breeze-wave canvas — layered sine waves in breeze blue,
 * rendered with low opacity so it's never overbearing.
 */
export const WaveCanvas = ({ className = "", opacity = 0.35 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let t = 0;
    const reduced = prefersReducedMotion();

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const waves = [
      { amp: 18, freq: 0.006, speed: 0.012, y: 0.55, alpha: 0.5, width: 2 },
      { amp: 26, freq: 0.004, speed: 0.008, y: 0.65, alpha: 0.35, width: 2.5 },
      { amp: 12, freq: 0.009, speed: 0.018, y: 0.45, alpha: 0.25, width: 1.5 },
    ];

    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      waves.forEach((wv) => {
        ctx.beginPath();
        for (let x = 0; x <= w; x += 4) {
          const y =
            h * wv.y + Math.sin(x * wv.freq + t * wv.speed * 60) * wv.amp;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, `rgba(41,171,226,${wv.alpha * 0.4})`);
        grad.addColorStop(0.5, `rgba(41,171,226,${wv.alpha})`);
        grad.addColorStop(1, `rgba(0,122,255,${wv.alpha * 0.4})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = wv.width;
        ctx.stroke();
      });
      t += 1 / 60;
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className={`pointer-events-none ${className}`}
      style={{ opacity }}
      aria-hidden="true"
    />
  );
};
